import { useState, useRef, useEffect, useCallback } from 'react';
import { rasterizeSvg, applyTransforms, cropImage, formatSize, getOutputSpec } from './imageCanvas';

/**
 * The non-visual half of an image upload flow: acquiring a source image, cropping it, applying
 * orientation transforms, keeping a live preview, and encoding the final file.
 *
 * Every upload modal shares this. What differs between them is the OUTPUT FORMAT and which
 * editing steps are meaningful, so the format is a parameter and the editing steps are rendered
 * by the caller.
 *
 * The caller owns any step-specific state of its own (background removal, colour replace,
 * quality) and hooks in through `onReset` and `onAfterCrop`.
 *
 * @param outputFormat  { type, extension, label, quality?, matte? }
 *                      `quality` applies to lossy types only. `matte` is REQUIRED for any opaque
 *                      type: canvas toBlob composites transparent pixels onto BLACK, so a source
 *                      with alpha would otherwise encode with black fringing.
 * @param onReset       called when the modal opens — clear caller-owned state here.
 * @param onAfterCrop   async (croppedDataUrl) => void, awaited after a crop is applied and before
 *                      the edit step renders. Somewhere to inspect the cropped image.
 */
export const useImageUpload = ({
  isOpen,
  onClose,
  onUpload,
  suggestedName = '',
  aspectRatio,
  outputSize,
  maxOutputHeight,
  maxOutputWidth,
  maxSizeMB = 5,
  allowedTypes,
  outputFormat,
  onReset,
  onAfterCrop,
}) => {
  // Source image
  const [sourceDataUrl, setSourceDataUrl] = useState(null);
  const [croppedDataUrl, setCroppedDataUrl] = useState(null);
  const [fileName, setFileName] = useState('');

  // Crop
  const [crop, setCrop] = useState(undefined);
  const cropImgRef = useRef(null);

  // Transforms
  const [rotation, setRotation] = useState(0);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);

  // Output
  const [previewUrl, setPreviewUrl] = useState(null);
  const [outputInfo, setOutputInfo] = useState(null);

  // UI
  const [step, setStep] = useState('select');
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const prevPreviewUrl = useRef(null);

  // Callers pass fresh closures every render; hold them in refs so they don't retrigger effects.
  const onResetRef = useRef(onReset);
  const onAfterCropRef = useRef(onAfterCrop);
  useEffect(() => { onResetRef.current = onReset; onAfterCropRef.current = onAfterCrop; });

  const constraints = (maxOutputHeight || maxOutputWidth)
    ? { maxHeight: maxOutputHeight, maxWidth: maxOutputWidth }
    : undefined;

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setSourceDataUrl(null); setCroppedDataUrl(null);
      setFileName(suggestedName);
      setCrop(undefined);
      setRotation(0); setFlipH(false); setFlipV(false);
      setPreviewUrl(null); setOutputInfo(null);
      setStep('select'); setDragOver(false); setUploading(false); setError(null);
      onResetRef.current?.();
    }
  }, [isOpen, suggestedName]);

  // Rebuild preview when the working image or transforms change (edit step only).
  // Note this fires for caller-driven pixel edits too: those replace croppedDataUrl, which is a dep.
  useEffect(() => {
    const src = croppedDataUrl || sourceDataUrl;
    if (!src || step !== 'edit') return;

    applyTransforms(src, { rotation, flipH, flipV }, constraints, outputFormat)
      .then(({ blob, width, height }) => {
        if (prevPreviewUrl.current) URL.revokeObjectURL(prevPreviewUrl.current);
        const url = URL.createObjectURL(blob);
        prevPreviewUrl.current = url;
        setPreviewUrl(url);
        setOutputInfo({ size: formatSize(blob.size), width, height, overLimit: blob.size > maxSizeMB * 1024 * 1024 });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [croppedDataUrl, sourceDataUrl, rotation, flipH, flipV, step, maxSizeMB,
      maxOutputHeight, maxOutputWidth, outputFormat.type, outputFormat.quality, outputFormat.matte]);

  // Release the last preview blob URL when the modal unmounts.
  useEffect(() => () => {
    if (prevPreviewUrl.current) URL.revokeObjectURL(prevPreviewUrl.current);
  }, []);

  const validateFile = useCallback((f) => {
    const ext = '.' + f.name.split('.').pop().toLowerCase();
    const allowed = allowedTypes.split(',').map(t => t.trim());
    if (!allowed.includes(ext)) { setError(`File type ${ext} not allowed. Use: ${allowed.join(', ')}`); return false; }
    return true;
  }, [allowedTypes]);

  const handleFileSelect = useCallback((f) => {
    setError(null);
    if (!validateFile(f)) return;
    const ext = f.name.split('.').pop().toLowerCase();
    const svg = ext === 'svg';
    if (!fileName) setFileName(suggestedName || f.name.replace(/\.[^.]+$/, ''));

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setCroppedDataUrl(null);
      setCrop(undefined);
      if (svg) {
        // Rasterize SVG to PNG so it can be cropped and edited like any other image
        rasterizeSvg(dataUrl).then(pngDataUrl => {
          setSourceDataUrl(pngDataUrl);
          setStep('crop');
        }).catch(() => {
          setError('Failed to process SVG');
        });
      } else {
        setSourceDataUrl(dataUrl);
        setStep('crop');
      }
    };
    reader.readAsDataURL(f);
  }, [validateFile, fileName, suggestedName]);

  // Ctrl+V paste
  useEffect(() => {
    if (!isOpen || step !== 'select') return;
    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const blob = item.getAsFile();
          if (blob) handleFileSelect(new File([blob], (suggestedName || 'pasted-image') + '.png', { type: blob.type }));
          break;
        }
      }
    };
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [isOpen, step, handleFileSelect, suggestedName]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files[0]) handleFileSelect(e.dataTransfer.files[0]);
  };

  const handlePasteButton = () => {
    navigator.clipboard.read().then(items => {
      for (const item of items) {
        const imgType = item.types.find(t => t.startsWith('image/'));
        if (imgType) { item.getType(imgType).then(blob => handleFileSelect(new File([blob], (suggestedName || 'pasted-image') + '.png', { type: blob.type }))); break; }
      }
    }).catch(() => setError('No image in clipboard or permission denied'));
  };

  // When image loads in cropper, set initial crop to full image
  const onCropImageLoad = useCallback((e) => {
    cropImgRef.current = e.currentTarget;
    const { width, height } = e.currentTarget;
    if (aspectRatio) {
      const imgAspect = width / height;
      let cropW, cropH;
      if (imgAspect > aspectRatio) {
        cropH = height; cropW = height * aspectRatio;
      } else {
        cropW = width; cropH = width / aspectRatio;
      }
      setCrop({ unit: 'px', x: (width - cropW) / 2, y: (height - cropH) / 2, width: cropW, height: cropH });
    } else {
      setCrop({ unit: 'px', x: 0, y: 0, width, height });
    }
  }, [aspectRatio]);

  const handleCropApply = async () => {
    if (!crop || !crop.width || !crop.height || !sourceDataUrl || !cropImgRef.current) return;
    try {
      const displayedWidth = cropImgRef.current.width;
      const displayedHeight = cropImgRef.current.height;
      const blob = await cropImage(sourceDataUrl, crop, displayedWidth, displayedHeight, outputSize);
      const reader = new FileReader();
      reader.onload = async () => {
        const dataUrl = reader.result;
        setCroppedDataUrl(dataUrl);
        await onAfterCropRef.current?.(dataUrl);
        setStep('edit');
      };
      reader.readAsDataURL(blob);
    } catch { setError('Crop failed'); }
  };

  const handleUpload = async () => {
    setUploading(true); setStep('uploading'); setError(null);
    try {
      const src = croppedDataUrl || sourceDataUrl;
      const { blob } = await applyTransforms(src, { rotation, flipH, flipV }, constraints, outputFormat);
      if (blob.size > maxSizeMB * 1024 * 1024) {
        setError(`Final image is ${formatSize(blob.size)} — exceeds ${maxSizeMB}MB limit.`);
        setStep('edit'); setUploading(false); return;
      }
      const safeName = (fileName || 'image').replace(/[^a-zA-Z0-9_-]/g, '-');
      const finalFile = new File([blob], `${safeName}.${outputFormat.extension}`, { type: outputFormat.type });
      await onUpload(finalFile);
      onClose();
    } catch (err) {
      if (!err?.notified) setError(typeof err === 'string' ? err : err?.message || 'Upload failed');
      setStep('edit');
    } finally { setUploading(false); }
  };

  return {
    // source / working image — callers replace croppedDataUrl to push a pixel edit through
    sourceDataUrl, croppedDataUrl, setCroppedDataUrl,
    workingSrc: croppedDataUrl || sourceDataUrl,
    fileName, setFileName,

    // crop
    crop, setCrop, cropImgRef,
    hasCrop: !!(crop && crop.width > 0 && crop.height > 0),

    // transforms
    rotation, setRotation, flipH, setFlipH, flipV, setFlipV,

    // output
    previewUrl, outputInfo,
    outputSpec: getOutputSpec(aspectRatio, outputSize, maxOutputHeight, maxOutputWidth),

    // ui
    step, setStep, dragOver, setDragOver, uploading, error, setError, inputRef,

    // handlers
    handleFileSelect, handleDrop, handlePasteButton,
    onCropImageLoad, handleCropApply, handleUpload,
  };
};
